import type { StartggInfo } from "./types";

// TOs running community auto-float tooling must model an amateur ("Silver")
// bracket as a *phase* of the main event rather than a sibling event, so
// grouping on the event alone collapses Pools/Gold/Silver into one list. Group
// on the (event, phase) pair, which is correct either way.

/** Real context.json files predate some fields. */
type PartialStartgg = Partial<StartggInfo> | null | undefined;

const UNKNOWN = "Unknown";
const DEFAULT_EVENT = "melee singles";

export interface SetGroupKey {
  key: string;
  eventId: string;
  eventName: string;
  phaseName: string | null;
}

/** Identify a set's bracket, preferring ids and falling back to names. */
export function setGroupKey(startgg: PartialStartgg): SetGroupKey {
  const event = startgg?.event;
  const phase = startgg?.phase;
  const eventId = String(event?.id ?? event?.name ?? UNKNOWN);
  const phaseId = phase?.id ?? phase?.name ?? "";
  return {
    key: `${eventId}::${phaseId}`,
    eventId,
    eventName: event?.name ?? UNKNOWN,
    phaseName: phase?.name ?? null,
  };
}

export interface SetGroup<T> {
  key: string;
  /** Heading, e.g. `"Pools"` or `"Melee Doubles · Bracket"`. */
  label: string;
  items: T[];
  /** Earliest set's start time; drives group ordering. */
  firstMs: number;
}

/**
 * Group sets by bracket, oldest first so pools precede bracket. Item order
 * within a group is preserved. Labels are prefixed with the event name only
 * when the input spans more than one event.
 */
export function groupByPhase<T>(
  items: T[],
  get: (item: T) => { startgg: PartialStartgg; startMs: number },
): SetGroup<T>[] {
  const groups = new Map<string, SetGroup<T> & { eventId: string; eventName: string }>();

  for (const item of items) {
    const { startgg, startMs } = get(item);
    const id = setGroupKey(startgg);
    const existing = groups.get(id.key);
    if (existing) {
      existing.items.push(item);
      if (startMs < existing.firstMs) existing.firstMs = startMs;
    } else {
      groups.set(id.key, {
        key: id.key,
        label: id.phaseName ?? id.eventName,
        items: [item],
        firstMs: startMs,
        eventId: id.eventId,
        eventName: id.eventName,
      });
    }
  }

  const multiEvent = new Set(Array.from(groups.values(), (g) => g.eventId)).size > 1;

  return Array.from(groups.values())
    .map((g) => ({
      key: g.key,
      label: multiEvent && g.label !== g.eventName ? `${g.eventName} · ${g.label}` : g.label,
      items: g.items,
      firstMs: g.firstMs,
    }))
    .sort((a, b) => a.firstMs - b.firstMs || a.label.localeCompare(b.label));
}

/**
 * Bracket qualifier for one set in isolation, where no group heading carries it
 * - e.g. `"Silver Bracket"`. Null when the set has no peers to distinguish it
 * from, per start.gg's `hasSiblings` flags.
 */
export function setBracketLabel(startgg: PartialStartgg): string | null {
  const event = startgg?.event;
  const phase = startgg?.phase;
  const parts: string[] = [];
  if (event?.hasSiblings && event.name) parts.push(event.name);
  if (phase?.hasSiblings && phase.name) parts.push(phase.name);
  if (parts.length > 0) return parts.join(" · ");

  // Contexts predating `hasSiblings`: name any non-default event.
  const name = event?.name;
  if (name && name.toLowerCase() !== DEFAULT_EVENT) return name;
  return null;
}
